"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, ShieldCheck, PhoneCall, ArrowRight, CircleCheck as CheckCircle2, Wrench, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyMetrics = [{
  id: "metric-response",
  label: "Local Dispatch Time",
  value: "5-10 Min",
  description: "Rapid Clayton County response",
  icon: Clock
}, {
  id: "metric-zip",
  label: "Primary Zone",
  value: "30236",
  description: "Jonesboro core service hub",
  icon: MapPin
}, {
  id: "metric-availability",
  label: "Availability",
  value: "24/7/365",
  description: "Day, night, weekend, and holiday",
  icon: Zap
}, {
  id: "metric-satisfaction",
  label: "Local Reputation",
  value: "100%",
  description: "Licensed and insured master plumbers",
  icon: ShieldCheck
}];
const trustReasons = [{
  id: "reason-hq",
  title: "Stationed in Jonesboro",
  description: "Unlike regional franchises routing calls from distant cities, our service team dispatches directly within Jonesboro, eliminating long delays during critical plumbing failures.",
  icon: MapPin
}, {
  id: "reason-infrastructure",
  title: "Clayton County System Expertise",
  description: "We know local water pressure variables, legacy piping profiles in established neighborhoods, and Clayton County municipal plumbing codes inside and out.",
  icon: Wrench
}, {
  id: "reason-readiness",
  title: "Fully Stocked Fleet for First-Visit Fixes",
  description: "Every emergency vehicle is outfitted with specialized leak detection gear, sewer cameras, high-output drain augers, and replacement water heater components.",
  icon: CheckCircle2
}];
export default function FlagshipWelcome() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="jonesboroflagshipwelcome" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header Row */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 font-medium text-secondary-foreground">
            <MapPin className="size-3.5 text-accent-foreground" />
            <span>Primary Service Hub: Jonesboro, GA</span>
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Established Local Presence in Jonesboro, Georgia
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Headquartered right in Jonesboro, we deliver immediate emergency response and lasting repairs across zip code 30236 and neighboring areas. Our team brings deep community roots and dedicated equipment to resolve pressing plumbing issues immediately.
          </p>
        </motion.div>

        {/* Key Metrics Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {keyMetrics.map((metric, index) => {
          const IconComponent = metric.icon;
          return <motion.div key={metric.id} variants={itemVariants} data-index={index}>
                <Card className="h-full border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                  <CardContent className="flex flex-col items-center p-6 text-center">
                    <div className="mb-3 flex size-12 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                      <IconComponent className="size-6 text-primary" />
                    </div>
                    <span className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                      {metric.value}
                    </span>
                    <span className="mt-1 text-sm font-semibold text-foreground">
                      {metric.label}
                    </span>
                    <span className="mt-1 text-xs text-muted-foreground">
                      {metric.description}
                    </span>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Split Content & Image Section */}
        <div className="mt-16 grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Why Jonesboro Trusts Us */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="space-y-6 lg:col-span-7">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Rapid Local Dispatch
              </span>
              <h3 className="mt-1 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Why Jonesboro Homeowners & Businesses Rely on Our Crew
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                Plumbing emergencies require immediate action before water damage destroys walls, flooring, and foundations. Because our dispatch center is situated right in Jonesboro, we avoid highway bottlenecks and arrive on-site with all necessary diagnostic machinery.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {trustReasons.map((reason, index) => {
              const ReasonIcon = reason.icon;
              return <div key={reason.id} data-index={index} className="flex items-start gap-4 rounded-lg border border-border bg-card p-4 transition-[border-color] duration-200 hover:border-primary/30">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                      <ReasonIcon className="size-5 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-base font-semibold text-card-foreground">
                        {reason.title}
                      </h4>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {reason.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <Separator className="my-6 bg-border" />

            {/* Action Bar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground shadow-sm">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call Jonesboro HQ Now</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/service-areas/" className="inline-flex items-center gap-2">
                  <span>View All Service Areas</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Visual Feature Card */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <Card className="overflow-hidden border-border bg-card p-0 shadow-md">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_van_dispatch.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_van_dispatch.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_van_dispatch.png-wvc-sizes" alt="Jonesboro Emergency Plumbing service van dispatched locally in zip code 30236" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-primary text-primary-foreground shadow-xs">
                    Flagship HQ Location
                  </Badge>
                </div>
              </div>

              <CardHeader className="p-6 pb-2">
                <CardTitle className="text-lg font-bold text-card-foreground">
                  Jonesboro Coverage Priority
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-6 pt-0">
                <p className="text-sm text-muted-foreground">
                  Our mobile units are continually routed through Tara Boulevard, Main Street, and Lake Spivey corridors to maintain our consistent 5 to 10 minute dispatch guarantee.
                </p>
                <div className="rounded-md border border-border bg-secondary/60 p-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-secondary-foreground">
                    <span>Central Hub Zone</span>
                    <span className="text-primary font-bold">Zip Code 30236</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Average Arrival Window</span>
                    <span className="font-medium text-foreground">5 - 10 Minutes</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}